import React, { useState, useEffect, useRef } from 'react';
import { Menu, Bell, Search, X, LogOut, User, Settings, ChevronDown, AlertTriangle, FileText, Clock } from 'lucide-react';
import { Input } from '../ui/input';
import api from '../../services/api';
import DataHealthIndicator from '../DataHealthIndicator';

export const Header = ({ onMenuClick, title }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [hasViewedNotifications, setHasViewedNotifications] = useState(false);
  const [adminInfo, setAdminInfo] = useState({ firstName: 'Admin', lastName: '', role: 'admin' });
  const profileRef = useRef(null);
  const notificationRef = useRef(null);

  useEffect(() => {
    fetchCurrentUserInfo();
    fetchExpiringDocuments();
    
    // Refresh notifications every 5 minutes
    const notifInterval = setInterval(fetchExpiringDocuments, 5 * 60 * 1000);
    
    // Listen for user profile updates
    const handleUserUpdate = () => {
      fetchCurrentUserInfo();
    };
    
    window.addEventListener('userUpdated', handleUserUpdate);
    window.addEventListener('settingsUpdated', handleUserUpdate);
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate);
      window.removeEventListener('settingsUpdated', handleUserUpdate);
      document.removeEventListener('mousedown', handleClickOutside);
      clearInterval(notifInterval);
    };
  }, []);

  const fetchExpiringDocuments = async () => {
    try {
      const response = await api.get('/notifications/expiring-documents');
      const newNotifications = response.data.notifications || [];
      const newCount = response.data.total || 0;
      
      // Only show badge if there are new notifications since last view
      if (newCount > notifications.length) {
        setHasViewedNotifications(false);
      }
      
      setNotifications(newNotifications);
      setNotificationCount(newCount);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const handleBellClick = () => {
    setShowNotifications(!showNotifications);
    if (!showNotifications) {
      setHasViewedNotifications(true);
    }
  };

  const fetchCurrentUserInfo = async () => {
    try {
      // First get user from localStorage
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (storedUser.email) {
        // Fetch fresh user data from server using email
        const response = await api.get('/admin/users');
        const currentUser = response.data.find(u => u.email === storedUser.email);
        
        if (currentUser) {
          setAdminInfo({
            firstName: currentUser.name?.split(' ')[0] || 'Admin',
            lastName: currentUser.name?.split(' ').slice(1).join(' ') || '',
            role: currentUser.role || storedUser.user_type || 'admin'
          });
          
          // Update localStorage with fresh data
          localStorage.setItem('user', JSON.stringify({
            ...storedUser,
            name: currentUser.name,
            user_id: currentUser.id
          }));
        } else if (storedUser.name) {
          // Use stored name if user not found in users list
          setAdminInfo({
            firstName: storedUser.name.split(' ')[0] || 'Admin',
            lastName: storedUser.name.split(' ').slice(1).join(' ') || '',
            role: storedUser.user_type || 'admin'
          });
        }
      } else {
        // Fallback to settings
        const response = await api.get('/settings');
        setAdminInfo(prev => ({
          ...prev,
          firstName: response.data.admin_first_name || 'Admin',
          lastName: response.data.admin_last_name || ''
        }));
      }
    } catch (error) {
      // Use localStorage as fallback
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (storedUser.name) {
        setAdminInfo({
          firstName: storedUser.name.split(' ')[0] || 'Admin',
          lastName: storedUser.name.split(' ').slice(1).join(' ') || '',
          role: storedUser.user_type || 'admin'
        });
      }
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    window.location.href = '/login';
  };

  // Get role display name
  const getRoleDisplay = () => {
    const roleMap = {
      'admin': 'Administrator',
      'dispatcher': 'Dispatcher',
      'driver': 'Shofer'
    };
    return roleMap[adminInfo.role] || 'Dispatcher';
  };

  // Get initials
  const getInitials = () => {
    const first = adminInfo.firstName?.charAt(0) || 'A';
    const last = adminInfo.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'AD';
  };

  // Get display name
  const getDisplayName = () => {
    if (adminInfo.lastName) {
      return `${adminInfo.firstName} ${adminInfo.lastName}`;
    }
    return adminInfo.firstName || 'Admin';
  };

  return (
    <header className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-6 sticky top-0 z-30">
      <div className="flex items-center gap-4">
        <button 
          className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
          onClick={onMenuClick}
        >
          <Menu className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }} data-testid="header-title">
          {title || 'Përmbledhje'}
        </h1>
      </div>

      <div className="flex items-center gap-3">
        {/* Data Health Indicator */}
        <DataHealthIndicator size="small" />
        
        {/* Search */}
        {showSearch ? (
          <div className="relative">
            <Input 
              type="text"
              placeholder="Kërko..."
              className="w-64 h-10 pr-10"
              autoFocus
            />
            <button 
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
              onClick={() => setShowSearch(false)}
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        ) : (
          <button 
            className="p-2 hover:bg-gray-100 rounded-lg"
            onClick={() => setShowSearch(true)}
          >
            <Search className="w-5 h-5 text-gray-500" />
          </button>
        )}

        {/* Notifications */}
        <div className="relative" ref={notificationRef}>
          <button 
            className="p-2 hover:bg-gray-100 rounded-lg relative"
            onClick={handleBellClick}
          >
            <Bell className="w-5 h-5 text-gray-500" />
            {notificationCount > 0 && !hasViewedNotifications && (
              <span 
                className="absolute top-1 right-1 w-4 h-4 text-white text-xs rounded-full flex items-center justify-center"
                style={{ background: '#dc2313' }}
              >
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </button>
          
          {/* Notifications Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border z-50 overflow-hidden">
              <div className="p-3 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-900">Njoftimet</h3>
                <p className="text-xs text-gray-500">{notificationCount} njoftime për dokumenta</p>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Nuk ka njoftime</p>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div 
                      key={notif.id}
                      className={`p-3 border-b last:border-0 hover:bg-gray-50 cursor-pointer ${
                        notif.severity === 'critical' ? 'bg-red-50' : 
                        notif.severity === 'high' ? 'bg-yellow-50' : ''
                      }`}
                      onClick={() => {
                        setShowNotifications(false);
                        window.location.href = `/admin/fleet/${notif.vehicle_id}/profile`;
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          notif.severity === 'critical' ? 'bg-red-100' :
                          notif.severity === 'high' ? 'bg-yellow-100' : 'bg-blue-100'
                        }`}>
                          {notif.severity === 'critical' ? (
                            <AlertTriangle size={16} className="text-red-600" />
                          ) : (
                            <Clock size={16} className={notif.severity === 'high' ? 'text-yellow-600' : 'text-blue-600'} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{notif.title}</p>
                          <p className="text-xs text-gray-600">{notif.message}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {notifications.length > 0 && (
                <div className="p-2 border-t bg-gray-50">
                  <button 
                    className="w-full text-center text-sm text-red-600 hover:text-red-700 py-1"
                    onClick={() => window.location.href = '/admin/fleet'}
                  >
                    Shiko të gjitha automjetet
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Profile - Clickable with Dropdown */}
        <div className="relative" ref={profileRef}>
          <button 
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center gap-3 pl-3 border-l border-gray-200 hover:bg-gray-50 rounded-lg pr-2 py-1 transition-colors cursor-pointer"
            data-testid="profile-dropdown-trigger"
          >
            <div 
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold"
              style={{ background: 'var(--gradient-primary)', color: 'var(--accent-text)' }}
            >
              {getInitials()}
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{getDisplayName()}</p>
              <p className="text-xs text-gray-500">{getRoleDisplay()}</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {showProfileMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 animate-fade-in">
              {/* User Info Header */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ background: 'var(--gradient-primary)', color: 'var(--accent-text)' }}
                  >
                    {getInitials()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{getDisplayName()}</p>
                    <p className="text-xs text-gray-500">{getRoleDisplay()}</p>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <button 
                  onClick={() => { setShowProfileMenu(false); window.location.href = '/admin/settings'; }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <User className="w-4 h-4 text-gray-400" />
                  <span>Profili im</span>
                </button>
                <button 
                  onClick={() => { setShowProfileMenu(false); window.location.href = '/admin/settings'; }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Settings className="w-4 h-4 text-gray-400" />
                  <span>Konfigurime</span>
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-gray-100 pt-1">
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Dil nga llogaria</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add fade-in animation style */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out forwards;
        }
      `}</style>
    </header>
  );
};

export default Header;
